import React, { useCallback, useContext, useEffect, useMemo, useRef } from 'react';
import { Row } from "tea-component";
import { shallowEqual, useSelector } from 'umi';
import CountryRegionRowItem from '@/pages/dashboard/components/CountryRegionRowItem';
import useData from './useData';
import { FilterContext } from '@/pages/dashboard/components/PageContainer/context';
import { PERIODTIMEOBJ } from '@/pages/dashboard/_config/constant';
import { useCommonRankShowData } from './_hooks';
import { useCommonRankMetricsConfig } from './useCommonRankMetricsConfig';
import { formatExeclCommon, formatExeclNormalPercent, formatExeclNum, formatExeclSharePercent, handleDownload } from '../../utils/utils';
import { useSelectorMap } from '../../utils/customHook';
import dataFormat from '@home/utils/dbDataFormat/dataFormat';
import { dynamicString } from '@home/utils/dynamicString';

import './index.scss';

export interface CountriesContextsProps {
  data: any;
  config: any;
}

const CommonRankListCo = (props: CountriesContextsProps) => {
  const { data: comData, config } = props;
  const { regionDataType = 'country', tabMsg } = comData || {};
  const { componentMetrics, api, comConfig, chartDataKey, tableDataKey, extralParams, formatData, isUseTableDownload, metric, showConfig, titleNameMsg, isUserQuota, showMapDimensions } = config || {};
  const { getFilterItemText } = useSelectorMap();
  const contextObj = useContext(FilterContext);
  const { filterVal, filterLoading } = contextObj || {};
  const { timeType = 'Day', rangeTime } = filterVal || {};
  const { gameCode } = useSelector((state: any) => ({
    gameCode: state.dashboard?.currentGame?.game_code,
  }), shallowEqual);
  const regionDataTypeRef = useRef(regionDataType);
  // 解决切换tab的时候数据历史数据未清楚，会一闪而过的问题
  const regionDataTypeUpdate = regionDataTypeRef?.current !== regionDataType;

  useEffect(() => {
    regionDataTypeRef.current = regionDataType;
  }, [regionDataType]);

  const compData = useData(
    filterVal,
    filterLoading,
    api,
    extralParams,
    PERIODTIMEOBJ[timeType],
    regionDataType,
    chartDataKey,
    tableDataKey,
    formatData,
  );

  const { data, loading, error } = compData || {};
  const {
    title,
    prefix,
    rangeTitle,
    chartTooltipsName,
    titleContent,
    tableColumns: defaultTableColumns,
    isSingle,
  } = useCommonRankShowData({
    accumated: regionDataType,
    filterVal,
    gameCode,
    componentMetrics,
    titleNameMsg,
    tabMsg,
    data: comData
  });

  const metricsConfig = useCommonRankMetricsConfig({
    componentMetrics,
    comConfig,
    accumated: regionDataType,
    data: comData,
    title,
    rangeTitle,
    isSingle,
    tabMsg,
    timeType,
    chartTooltipsName,
  });

  const tableColumns =
    metricsConfig?.hasFormatRuleConfig && metricsConfig?.tableColumns
      ? metricsConfig?.tableColumns
      : defaultTableColumns;

  const chartTooltipsConfigToUse =
    metricsConfig?.hasFormatRuleConfig && metricsConfig?.chartTooltipsConfig
      ? metricsConfig?.chartTooltipsConfig
      : {
        tooltipsName: chartTooltipsName,
        tooltipsType: componentMetrics?.type,
        tooltipsDecimal: componentMetrics?.decimal,
        tooltipsPercentDecimals: componentMetrics?.percentDecimals,
      };

  // 世界地图展示维度：通过 showMapDimensions 配置，默认仅 country 维度
  const mapDimensions = Array.isArray(showMapDimensions) ? showMapDimensions : ['country'];
  const shouldShowMap = mapDimensions.includes(regionDataType);
  const comConfigToUse = useMemo(
    () => ({
      ...comConfig,
      isHaveMap: shouldShowMap && (comConfig?.isHaveMap ?? false),
    }),
    [comConfig, shouldShowMap]
  );
  const showConfigToUse = useMemo(
    () => ({
      ...showConfig,
      showMap: shouldShowMap && (showConfig?.showMap ?? false),
    }),
    [showConfig, shouldShowMap]
  );

  const downloadFunc = useCallback(() => {
    const hasOthersValue = data?.tableData?.find((item: { name: string; }) => item?.name === 'Others')?.value !== '';
    const downloadDatas: any = [];
    const resolvedFormatRule = componentMetrics?.formatRule
      ? dynamicString(componentMetrics.formatRule, comData)
      : '';
    data?.tableData?.forEach((item: any) => {
      if (!hasOthersValue && item?.full_name === 'Others') return;
      const values = item?.value;
      let metricValue: string;
      if (resolvedFormatRule) {
        metricValue = String(dataFormat({ value: values, formatRule: resolvedFormatRule }) ?? '');
      } else if (componentMetrics?.type === 'percent') {
        metricValue = String(formatExeclNormalPercent(values) ?? '');
      } else if (componentMetrics?.type === 'int') {
        metricValue = String(formatExeclNum(values) ?? '');
      } else {
        metricValue = String(formatExeclCommon(values) ?? '');
      }
      const row: Record<string, any> = {
        name: getFilterItemText(regionDataType, item?.name, item?.full_name, true),
        [metric]: metricValue,
        share_of_all: formatExeclSharePercent(item?.share_of_all),
      };
      downloadDatas.push(row);
    });
    const tableConfig: {
      option: any;
      downloadData: any;
    } = {
      option: tableColumns,
      downloadData: downloadDatas
    };
    handleDownload(tableConfig, titleContent?.name, false);
  }, [data, metric, tableColumns, regionDataType, componentMetrics, comData]);

  return (
    <Row gap={24} className="dbd-breakdown-box">
      <CountryRegionRowItem
        prefix={prefix}
        titleName={title}
        titleContent={titleContent}
        regionDataType={regionDataType === 'system' ? 'os' : regionDataType}
        comConfig={comConfigToUse}
        timeRange={rangeTime}
        initActiveIndex={0}
        loading={regionDataTypeUpdate || loading}
        isSingle={isSingle}
        isError={error}
        downloadFunc={isUseTableDownload ? downloadFunc : null}
        data={data}
        isUserQuota={isUserQuota || componentMetrics?.type === 'int'}
        timeType={filterVal?.timeType}
        chartTooltipsConfig={chartTooltipsConfigToUse}
        tableColumns={tableColumns}
        gameCode={gameCode}
        showConfig={showConfigToUse}
        morePie
        responeseValueName={componentMetrics?.responeseValueName || 'value'}
      />

    </Row>
  );
};

export default CommonRankListCo;